import React, { useState, useEffect } from "react";
import { Modal, Button, Typography, Tag, Alert, Spin } from "antd";
import { X, User, Wrench, Settings, Building2 } from "lucide-react";
import Icon from "./Icon";
import {
  getUserProfile,
  setUserRole,
  USER_ROLES,
} from "../services/userService";
import { useAuth } from "../auth/auth.context";
import ChangePasswordDialog from "./ChangePasswordDialog";

const { Text } = Typography;

const UserRoleDialog = ({ open, onClose, onRoleUpdated }) => {
  const { currentUser } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);

  // Load user profile when dialog opens
  useEffect(() => {
    if (open && currentUser) {
      loadUserProfile();
    }
  }, [open, currentUser]);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const profile = await getUserProfile();
      setUserProfile(profile);
      setSelectedRole(profile.role);
    } catch (err) {
      console.error("Error loading user profile:", err);
      setError("Failed to load user profile");
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (value) => {
    setSelectedRole(value);
  };

  const handleOpenChangePasswordDialog = () => {
    setChangePasswordDialog(true);
  };

  const handleCloseChangePasswordDialog = () => {
    setChangePasswordDialog(false);
  };

  const handleUpdateRole = async () => {
    if (!selectedRole || selectedRole === userProfile?.role) {
      return;
    }

    try {
      setUpdating(true);
      setError(null);

      await setUserRole(currentUser.uid, selectedRole);

      // Reload profile to get updated data
      await loadUserProfile();

      // Notify parent component
      if (onRoleUpdated) {
        onRoleUpdated(selectedRole);
      }

      // Show success message
      setError(null);
    } catch (err) {
      console.error("Error updating role:", err);
      setError("Failed to update role. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  const getRoleIcon = (role) => {
    const iconMap = {
      installer: (
        <Icon as={Wrench} style={{ color: "var(--ant-color-primary)" }} />
      ),
      engineer: (
        <Icon as={Settings} style={{ color: "var(--ant-color-info)" }} />
      ),
      architect: (
        <Icon as={Building2} style={{ color: "var(--ant-color-info)" }} />
      ),
      salesperson: (
        <Icon as={Building2} style={{ color: "var(--ant-color-success)" }} />
      ),
    };

    return iconMap[role] || <Icon as={User} />;
  };

  const getRoleColor = (role) => {
    const colorMap = {
      installer: "processing",
      engineer: "purple",
      architect: "cyan",
      salesperson: "success",
    };

    return colorMap[role] || "default";
  };

  const formatRoleName = (role) => {
    const nameMap = {
      installer: "Installer",
      engineer: "Engineer",
      architect: "Architect",
      salesperson: "Sales Person",
    };

    return nameMap[role] || role;
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon as={User} style={{ color: "var(--ant-color-primary)" }} />
          <span>User Information</span>
        </div>
      }
      className="airsmart-dialog"
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button type="primary" onClick={handleOpenChangePasswordDialog}>
            Change Password
          </Button>
          <Button onClick={onClose}>Cancel</Button>
          {!loading && userProfile && selectedRole !== userProfile.role && (
            <Button
              onClick={handleUpdateRole}
              type="primary"
              loading={updating}
            >
              {updating ? "Updating..." : "Update Role"}
            </Button>
          )}
        </div>
      }
      width={576}
      closeIcon={<Icon as={X} />}
      destroyOnClose
    >
      <ChangePasswordDialog
        open={changePasswordDialog}
        onClose={handleCloseChangePasswordDialog}
      />

      {/* Loading State */}
      {loading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "32px 0",
          }}
        >
          <Spin size="large" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* User Profile */}
      {!loading && userProfile && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ display: "block" }}>
              Email
            </Text>
            <Text>{userProfile.email}</Text>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ display: "block" }}>
              Display Name
            </Text>
            <Text>{userProfile.displayName || "Not set"}</Text>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ display: "block" }}>
              Current Role
            </Text>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 4,
              }}
            >
              {getRoleIcon(userProfile.role)}
              <Tag
                color={getRoleColor(userProfile.role)}
                bordered
              >
                {formatRoleName(userProfile.role)}
              </Tag>
            </div>
          </div>
        </div>
      )}

      {/* Role Selection */}
      {/* {!loading && userProfile && (
        <div>
          <Title level={5} style={{ marginBottom: 16 }}>
            Change Role
          </Title>

          <Select
            value={selectedRole}
            onChange={handleRoleChange}
            placeholder="Select Role"
            style={{ width: "100%", marginBottom: 16 }}
            options={Object.values(USER_ROLES).map((role) => ({
              value: role,
              label: (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {getRoleIcon(role)}
                  {formatRoleName(role)}
                </div>
              ),
            }))}
          />

          <Alert
            type="info"
            showIcon
            style={{ marginTop: 16 }}
            message={<Text strong>Role Descriptions:</Text>}
            description={
              <Text style={{ marginTop: 8, display: "block" }}>
                {"\u2022"} <Text strong>Installer:</Text> Access to installation guides and tools<br/>
                {"\u2022"} <Text strong>Engineer:</Text> Access to technical specifications and diagrams<br/>
                {"\u2022"} <Text strong>Architect:</Text> Access to design guidelines and CAD templates<br/>
                {"\u2022"} <Text strong>Sales Person:</Text> Access to brochures and pricing information
              </Text>
            }
          />
        </div>
      )} */}
    </Modal>
  );
};

export default UserRoleDialog;
